/**
 * autoPlaytester_v2.ts — Comprehensive Automated Playtester
 *
 * DROP THIS FILE INTO: client/src/game/autoPlaytester_v2.ts
 *
 * This bot plays the game automatically and collects EVERY piece of data:
 * - Every item picked, every item skipped, every item offered
 * - Every weapon fired, every weapon's DPS, every weapon upgrade
 * - Every enemy type killed, every enemy type that damaged the player
 * - Every level-up choice (what was offered vs what was picked)
 * - Every chest choice (what was offered vs what was picked)
 * - Every shrine choice (what was offered vs what was picked)
 * - Gold earned, gold spent, gold efficiency
 * - HP timeline (sampled every second)
 * - DPS timeline (sampled every second)
 * - Kill rate timeline (sampled every second)
 * - Per-run anomaly/bug detection
 * - Cross-run improvement tracking
 *
 * IMPORTS NEEDED IN engine.ts:
 *   import { initBot, updateBot, getBotReport, isBotActive } from './autoPlaytester_v2';
 *
 * HOOK POINTS IN engine.ts:
 *   1. In initGame():     initBot(state);
 *   2. In updateGame():   if (isBotActive()) updateBot(state, dt);
 *   3. In keydown handler: if (e.key === 'F9') toggleBot();
 *                          if (e.key === 'F10') printReport();
 *                          if (e.key === 'F11') downloadFullCSV();
 */

import type { GameState, Enemy } from './types';
import { GAME_VERSION } from './engine';
import { debugTimeScale } from './debugState';
import {
  isPendingLevelUp,
  getLevelUpOptionDisplayData,
  handleLevelUpSelection,
  isPendingShrineChoice,
  getShrineBuffDisplayOptions,
  handleShrineSelection,
  getChestOptionDisplayData,
  handleChestItemSelection,
  getPercState,
  getItemState,
  getAppliedBuffsList,
  getPercContributions,
  type ChestItemDisplay,
} from './itemsAdapter';
import type { LevelUpOptionDisplay } from './peanutPercSystem';
import { getWeaponSystemState } from './weaponAdapter';
import { getWaveSystemState, getEnemyCount } from './enemyAdapter';
import { getWeaponDef, computeWeaponStats, type WeaponInstance } from './weapons';
import { computeBalanceReport, type BalanceReport } from './balanceAnalyzer';
import { getLastV1Pick, BOT_BRAIN_VERSION } from './autoPlaytester';
// BETA 14.61 — CSV converge: pull WeaponLab tracker getters so BOT RUN CSV
// can emit the same rich per-mechanic data WL CSV has.
import {
  getLocalPercTracker, getLocalItemTracker, getLocalShrineTracker,
  getLocalFloorEffectTracker, getLocalEventLog, getLocalDeathContext,
  getLocalNearMissEvents, getLocalRetreatEvents,
  getLocalWeaponEnemyMatrix, getLocalSynergyTracker,
  computeTopWeaponSummary,
  // BETA 14.72 — bot diagnostics
  trackBotPosition, trackStuckEvent, trackBotMapCell,
  getLocalBotPositionTrail, getLocalStuckEvents, getLocalBotMapCellVisits,
  getLocalDecisionTrace,
  // BETA 14.74 — per-run reset to fix tracker cumulative bleed between runs.
  clearTrackers,
} from './weaponLab';

// ═══════════════════════════════════════════════════════════════════════════════
//  SECTION 1: DATA STRUCTURES — What we track per run
// ═══════════════════════════════════════════════════════════════════════════════

/** One choice event (level-up, chest, or shrine) */
interface ChoiceEvent {
  timestamp: number;          // seconds into the run
  type: 'levelup' | 'chest' | 'shrine';
  /** Every option that was offered (index 0, 1, 2) */
  offered: {
    index: number;
    name: string;
    id: string;
    rarity: string;
    type: string;             // 'new_perc' | 'upgrade_perc' | 'new_weapon' | 'weapon_upgrade' | 'item' | 'buff'
    description: string;
  }[];
  /** Which index the bot picked */
  pickedIndex: number;
  /** Name of what was picked */
  pickedName: string;
  /** Why the bot picked it (reasoning string) */
  reason: string;
  /** Was a reroll used? */
  rerolled: boolean;
}

/** Per-weapon tracking across a run */
interface WeaponRunData {
  weaponId: string;
  weaponName: string;
  acquiredAt: number;         // seconds into run when first obtained
  finalLevel: number;
  finalRarity: string;
  totalDamageDealt: number;   // from weapon system state
  totalShotsFired: number;
  totalShotsHit: number;      // BETA 13.43: shots that connected with at least 1 enemy. Hit rate = totalShotsHit / totalShotsFired.
  totalCrits: number;
  timesUpgraded: number;      // how many times this weapon was leveled up
  dpsOverTime: number[];      // DPS sampled every 5 seconds
}

/** Per-enemy-type tracking across a run */
interface EnemyTypeData {
  enemyType: string;
  totalKilled: number;
  totalDamageDealtToPlayer: number;
  firstSeenAt: number;        // seconds into run
  lastKilledAt: number;
  killsOverTime: number[];    // kills per 30-second window
  // Stats observed from live enemy instances (averaged across all seen)
  observedMaxHp: number;     // maxHp from first observed instance
  observedSpeed: number;     // speed from first observed instance
  observedDamage: number;    // damage from first observed instance
  firstSpawnAt: number;      // when this type was FIRST SEEN alive (not first killed)
  peakCount: number;         // maximum alive at once during the run
}

/** Per-item tracking */
interface ItemRunData {
  itemId: string;
  itemName: string;
  rarity: string;
  acquiredAt: number;         // seconds into run
  source: 'levelup' | 'chest' | 'shrine' | 'drop' | 'unknown';
  stacks: number;             // how many times this item was picked
}

/** Timeline sample (taken every 1 second) */
interface TimelineSample {
  t: number;                  // seconds into run
  hp: number;
  maxHp: number;
  shield: number;
  gold: number;
  level: number;
  kills: number;
  dps: number;                // total damage dealt / time
  enemyCount: number;         // enemies alive on screen
  phase: string;              // game phase at this moment
  wave: number;
  bossActive: boolean;
  swarmActive: boolean;
  totalSpawned: number;
}

/** Anomaly / bug detected during a run */
interface Anomaly {
  timestamp: number;
  severity: 'info' | 'warning' | 'bug';
  category: string;
  message: string;
  data?: Record<string, any>;
}

/** Discrete wave/swarm/boss event during a run */
interface WaveEvent {
  t: number;            // seconds into run
  event: 'wave_start' | 'swarm_start' | 'swarm_end' | 'boss_spawn' | 'boss_killed' | 'floor_advance' | 'final_swarm';
  wave: number;         // state.wave at time of event
  enemyCount: number;   // enemies alive at time of event
  detail?: string;      // e.g. boss type name
}

/** Complete data for one run */
interface RunData {
  runNumber: number;
  gameVersion: string;        // e.g. 'BETA 13.5' — which build produced this run
  botBrainVersion: string;    // e.g. '3.0' — which bot brain version made the decisions
  timeScale: number;          // debugTimeScale at run start (1 = normal, 5 = 5× speed)
  wallClockSec: number;       // real elapsed seconds (for reference)
  startedAt: number;          // Date.now()
  endedAt: number;
  characterId: string;
  characterName: string;

  // ── Summary stats ──
  survivalSeconds: number;    // game-time seconds (state.time delta, scale-aware)
  finalFloor: number;
  finalLevel: number;
  totalKills: number;
  totalGoldEarned: number;
  totalGoldSpent: number;
  totalDamageDealt: number;
  totalDamageTaken: number;
  totalHealingReceived: number;
  chestsOpened: number;
  shrinesUsed: number;
  rerollsUsed: number;
  deathCause: string;         // 'enemy:zombie', 'enemy:caseoh', 'timeout', 'unknown'
  deathEnemyType: string;     // specific enemy type that killed the player
  deathPosition: { x: number; y: number };

  // ── Detailed tracking ──
  choices: ChoiceEvent[];
  weapons: WeaponRunData[];
  enemyTypes: Record<string, EnemyTypeData>;
  items: ItemRunData[];
  timeline: TimelineSample[];
  anomalies: Anomaly[];
  waveEvents: WaveEvent[];

  // ── Balance analysis at death ──
  balanceScore: number;
  balanceLabel: string;
  avgPlayerTTK: number;
  avgEnemyTTK: number;
  killsPerMinute: number;
  peakEnemyCount: number;     // max enemies alive at once
  avgEnemyCount: number;      // average enemies alive per second
  totalSpawned: number;       // total enemies spawned (from waveState)
  bossesSpawned: number;      // total bosses this run
  totalSwarms: number;        // swarm events triggered
  finalWave: number;          // highest wave number reached

  // ── Final stat snapshot ──
  finalStats: {
    maxHp: number;
    damage: number;
    armor: number;
    moveSpeed: number;
    attackSpeed: number;
    critChance: number;
    critDamage: number;
    lifesteal: number;
    hpRegen: number;
    evasion: number;
    knockback: number;
    xpMultiplier: number;
    goldMultiplier: number;
    pickupRange: number;
    luck: number;
    thorns: number;
    shield: number;
  };

  // ── Perc (perk) state at death ──
  finalPercs: {
    percName: string;
    stat: string;
    level: number;
    totalBonus: number;
  }[];

  // ── Shrine buffs applied ──
  shrineBuffs: { stat: string; value: number; label: string }[];

  // ── BETA 14.61 — Engine state trackers (converge with WeaponLab CSV) ──
  // Snapshotted at endCurrentRun() so the bot CSV can emit the same rich
  // per-mechanic data that WeaponLab CSV has had since BETA 14.30+. Closes
  // the 67 → ~150 column converge gap PSBOT RUN flagged. Fields are JSON
  // strings ready for direct CSV emit.
  percTrackerJson?: string;
  itemTrackerJson?: string;
  shrineTrackerJson?: string;
  floorEffectTrackerJson?: string;
  weaponEnemyMatrixJson?: string;
  synergyTrackerJson?: string;
  eventLogJson?: string;
  deathContextJson?: string;
  nearMissEventsLogJson?: string;
  retreatEventsLogJson?: string;
  // Phase 1 stat snapshots — read from finalStatsBySource style fields
  finalDamagePct?: number;
  finalCritChancePct?: number;
  finalAttackSpeedPct?: number;
  finalMaxHP?: number;
  // Phase 1 event counters
  reviveEventsThisRun?: number;
  healEventsThisRun?: number;
  dodgeEventsThisRun?: number;
  phaseEventsThisRun?: number;
  knockbackEventsThisRun?: number;
  // Phase 1 enemy-attack outcomes
  enemyAttacksAttemptedThisRun?: number;
  enemyAttacksHitThisRun?: number;
  enemyAttacksMissedByEvasion?: number;
  enemyAttacksMissedByIframe?: number;
  enemyAttacksMissedByPhase?: number;
  // Engine perf
  engineFramePerfJson?: string;
  // Phase 5 timing baselines
  firstBloodTime?: number;
  firstDamageTakenTime?: number;
  longestStretchBetweenHitsMs?: number;
  // Top-line weapon×enemy summaries (computed from matrix at finalize)
  topWeaponVsZombie?: string;
  topWeaponVsTimmy?: string;
  topWeaponVsCaseoh?: string;
  topWeaponVsElite?: string;
  weakestWeaponOverall?: string;
  // BETA 14.72 — Bot diagnostics (snapshot at finalizeRun)
  botPositionTrailJson?: string;
  stuckEventsJson?: string;
  botMapCellVisitsJson?: string;
  decisionTraceJson?: string;
}

/** Cross-run analytics */
interface CrossRunAnalytics {
  totalRuns: number;
  avgSurvival: number;
  avgKills: number;
  avgLevel: number;
  avgFloor: number;
  avgGold: number;
  avgDamageDealt: number;
  avgDamageTaken: number;
  avgKillsPerMinute: number;
  bestRun: { runNumber: number; survivalSeconds: number; kills: number; level: number };
  worstRun: { runNumber: number; survivalSeconds: number; kills: number; level: number };
  // Item pick rates: how often each item was picked when offered
  itemPickRates: Record<string, { offered: number; picked: number; rate: number }>;
  // Weapon pick rates
  weaponPickRates: Record<string, { offered: number; picked: number; rate: number }>;
  // Most common death causes
  deathCauses: Record<string, number>;
  // Survival trend (does the bot improve over runs?)
  survivalTrend: number[];    // survivalSeconds per run in order
  killsTrend: number[];
  levelTrend: number[];
  // Anomaly summary
  totalAnomalies: number;
  anomalyCategories: Record<string, number>;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  SECTION 2: BOT STATE
// ═══════════════════════════════════════════════════════════════════════════════

let botActive = false;
let currentRun: RunData | null = null;
let allRuns: RunData[] = [];
let runCounter = 0;
const MAX_RUNS = 100;        // auto-download CSV when this many runs complete

const V2_STORAGE_KEY = 'goopzone_bot_v2_runs';

// ── Persistence helpers ──────────────────────────────────────────────────────

function saveRunsToStorage(): void {
  try {
    localStorage.setItem(V2_STORAGE_KEY, JSON.stringify(allRuns));
  } catch { /* storage full or unavailable */ }
}

function loadRunsFromStorage(): void {
  try {
    const raw = localStorage.getItem(V2_STORAGE_KEY);
    if (!raw) return;
    const loaded = JSON.parse(raw) as RunData[];
    if (Array.isArray(loaded) && loaded.length > 0) {
      allRuns = loaded;
      runCounter = loaded.length;
      console.log(`%c[BOT v2] 💾 Loaded ${loaded.length} saved runs from storage`, 'color:#00d4ff');
    }
  } catch { /* ignore corrupt data */ }
}

/** Returns true if there are saved runs in localStorage */
export function hasBotRunData(): boolean {
  try {
    const raw = localStorage.getItem(V2_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch { return false; }
}

/** How many runs are saved */
export function getSavedRunCount(): number {
  try {
    const raw = localStorage.getItem(V2_STORAGE_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch { return 0; }
}

/** Clear all saved run data from storage */
export function clearBotRunData(): void {
  localStorage.removeItem(V2_STORAGE_KEY);
  allRuns = [];
  runCounter = 0;
  // Also clear v1 bot run logs + run counter so the in-game DATA panel resets too
  localStorage.removeItem('goopzone_bot_runs_v1');
  localStorage.removeItem('goopzone_bot_run_count');
  console.log('%c[BOT v2] 🗑️ Run data cleared', 'color:#ff4444');
}

// Tracking helpers
let lastKillCount = 0;
let lastGold = 0;
let lastHp = 0;
let lastDamageDealt = 0;
let lastDamageTaken = 0;
// BETA 14.76 — staleness counters for stuck-event diagnostics. Track game-time
// of the bot's last successful kill / damage event. At a stuck moment, a high
// "seconds since" value is the smoking gun for "bot's been engaging without
// success" failure modes (no_target, weapon_oor).
let _lastKillTimeT = 0;
let _lastDamageDealtTimeT = 0;
let timelineTick = 0;        // accumulator for 1-second timeline samples
let choiceDebounce = 0;      // prevent double-picking

// ── Passive choice observation (v1 makes picks; v2 records what was offered) ──
let pendingOfferType: 'levelup' | 'chest' | 'shrine' | null = null;
let pendingOfferedOptions: ChoiceEvent['offered'] = [];
let pendingOfferTimestamp = 0;
let lastPhase = '';
let goldSpentThisRun = 0;
let healingThisRun = 0;
let rerollsUsedThisRun = 0;
let prevEnemySnapshot: Map<number, { hp: number; type: string }> = new Map();
let _lastWave = 0;              // detect wave number changes
let _lastBossActive = false;    // detect boss spawn/death
let _lastSwarmActive = false;   // detect swarm start/end
let _lastFloor = 0;             // detect floor advances
let _lastFinalSwarm = false;    // detect final swarm entry
let _enemyCountAccum = 0;       // sum of enemyCount samples for avg calculation
let _enemyCountSamples = 0;     // number of samples taken

// ═══════════════════════════════════════════════════════════════════════════════
//  SECTION 3: PUBLIC API
// ═══════════════════════════════════════════════════════════════════════════════

/** Called by v1 toggleBot / GameCanvas to keep data-bot in sync with the main bot toggle */
export function setBotV2Active(active: boolean): void {
  botActive = active;
}

/** BETA 20.88 — PSBOT cycle-30bg P0. The engine's 240s floor-auto-advance was
 *  gated only on V1's isBotEnabled(); the farm runs on V2, so it never fired
 *  and bots that survived past 240s were stuck on floor 1 forever (0 floor-2
 *  data). The gate now ORs in this getter. */
export function isBotV2Active(): boolean {
  return botActive;
}

export function initBotV2(state: GameState): void {
  // Load any previous runs from localStorage on first init
  // (runs accumulate across sessions until you hit MAX_RUNS or clear manually)
  if (allRuns.length === 0) loadRunsFromStorage();

  // Reset per-run tracking state (allRuns is preserved)
  lastKillCount = 0;
  lastGold = 0;
  lastHp = state.player.hp;
  lastDamageDealt = 0;
  lastDamageTaken = 0;
  // BETA 14.76 — staleness counters for stuck-event diagnostics
  _lastKillTimeT = 0;
  _lastDamageDealtTimeT = 0;
  timelineTick = 0;
  choiceDebounce = 0;
  lastPhase = state.phase;
  goldSpentThisRun = 0;
  healingThisRun = 0;
  rerollsUsedThisRun = 0;
  prevEnemySnapshot.clear();
}

/**
 * Call this when the player quits mid-run or closes the game.
 * Saves current data to localStorage and triggers an automatic CSV download.
 */
export function finalizeV2OnQuit(): void {
  if (allRuns.length === 0) return;
  saveRunsToStorage();
  console.log('%c[BOT v2] 💾 Auto-saving and downloading CSV on quit...', 'color:#ffcc00');
  downloadFullCSV();
}

/**
 * Called every frame from engine.ts updateGame() — BEFORE v1's updateBot.
 * This is a PASSIVE DATA COLLECTOR: v1 handles all movement and choice selection.
 * v2 only observes, records, and builds the 9-sheet CSV.
 */
export function updateBotV2(state: GameState, dt: number): void {
  if (!botActive) return;

  // ── Start a new run if needed ──
  if (!currentRun && state.phase === 'playing') {
    startNewRun(state);
  }

  if (!currentRun) return;

  // ── Phase transition detection (must happen before observeChoiceScreens) ──
  if (state.phase !== lastPhase) {
    handlePhaseTransition(lastPhase, state.phase, state);
    lastPhase = state.phase;
  }

  // ── Passively snapshot what was offered on choice screens ──
  // v1 will actually make the pick; we just record what options were presented.
  observeChoiceScreens(state);

  // ── Data tracking (playing phase only — movement handled entirely by v1) ──
  if (state.phase === 'playing') {
    trackCombatData(state, dt);
    trackWaveEvents(state);
  }

  // ── Timeline sampling (every 1 second) ──
  timelineTick += dt;
  if (timelineTick >= 1.0 && state.phase === 'playing') {
    timelineTick -= 1.0;
    sampleTimeline(state);
  }

  // ── Anomaly detection (every frame) ──
  detectAnomalies(state);

  // ── Death detection ──
  if (state.phase === 'gameover' && currentRun) {
    endCurrentRun(state);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  SECTION 4: RUN LIFECYCLE
// ═══════════════════════════════════════════════════════════════════════════════

function startNewRun(state: GameState): void {
  runCounter++;
  console.log(`%c[BOT] 🎮 Starting run #${runCounter}`, 'color:#00d4ff;font-size:12px');

  // BETA 14.74 — Tracker per-run reset (mirror of v1). weaponLab module-level
  // trackers are singletons; without explicit clear, run N's CSV row contains
  // events from runs 1..N-1. Phase 0 sanity check on cycle-9.5 CSV confirmed
  // the bleed. clearTrackers() also resets BETA 14.72 bot diagnostic streams.
  clearTrackers();

  currentRun = {
    runNumber: runCounter,
    gameVersion: GAME_VERSION,
    botBrainVersion: BOT_BRAIN_VERSION,
    timeScale: debugTimeScale,
    wallClockSec: 0,
    startedAt: Date.now(),
    endedAt: 0,
    characterId: state.character?.id ?? 'unknown',
    characterName: state.character?.name ?? 'Unknown',
    survivalSeconds: 0,
    finalFloor: 1,
    finalLevel: 1,
    totalKills: 0,
    totalGoldEarned: 0,
    totalGoldSpent: 0,
    totalDamageDealt: 0,
    totalDamageTaken: 0,
    totalHealingReceived: 0,
    chestsOpened: 0,
    shrinesUsed: 0,
    rerollsUsed: 0,
    deathCause: 'unknown',
    deathEnemyType: 'unknown',
    deathPosition: { x: 0, y: 0 },
    choices: [],
    weapons: [],
    enemyTypes: {},
    items: [],
    timeline: [],
    anomalies: [],
    balanceScore: 0,
    balanceLabel: '',
    avgPlayerTTK: 0,
    avgEnemyTTK: 0,
    killsPerMinute: 0,
    peakEnemyCount: 0,
    avgEnemyCount: 0,
    totalSpawned: 0,
    bossesSpawned: 0,
    totalSwarms: 0,
    finalWave: 1,
    waveEvents: [],
    finalStats: {
      maxHp: 0, damage: 0, armor: 0, moveSpeed: 0, attackSpeed: 0,
      critChance: 0, critDamage: 0, lifesteal: 0, hpRegen: 0, evasion: 0,
      knockback: 0, xpMultiplier: 0, goldMultiplier: 0, pickupRange: 0,
      luck: 0, thorns: 0, shield: 0,
    },
    finalPercs: [],
    shrineBuffs: [],
  };

  // Reset per-run trackers
  lastKillCount = state.killCount ?? 0;
  lastGold = state.player.gold ?? 0;
  lastHp = state.player.hp;
  lastDamageDealt = 0;
  lastDamageTaken = 0;
  // BETA 14.76 — staleness counters reset
  _lastKillTimeT = 0;
  _lastDamageDealtTimeT = 0;
  timelineTick = 0;
  goldSpentThisRun = 0;
  healingThisRun = 0;
  rerollsUsedThisRun = 0;
  prevEnemySnapshot.clear();
  _lastWave = state.wave ?? 1;
  _lastBossActive = false;
  _lastSwarmActive = false;
  _lastFloor = 0;
  _lastFinalSwarm = false;
  _enemyCountAccum = 0;
  _enemyCountSamples = 0;

  // Snapshot initial weapons
  snapshotWeapons(state, 0);
}

function endCurrentRun(state: GameState): void {
  if (!currentRun) return;

  // ── BETA 19.69 — Refresh characterId/Name from CURRENT state at run-end.
  // Was captured ONCE at startNewRun() which fired the first frame phase==='playing'
  // — but state.character could still be the previous run's value (stale) or get
  // swapped mid-run via __startGame/URL-param. The CSV row was mislabeling runs.
  // Re-reading at run-end ensures the reported character matches who actually played.
  if (state.character) {
    currentRun.characterId = state.character.id ?? currentRun.characterId;
    currentRun.characterName = state.character.name ?? currentRun.characterName;
  }

  const t = state.time;
  currentRun.endedAt = Date.now();
  currentRun.wallClockSec = Math.round((currentRun.endedAt - currentRun.startedAt) / 100) / 10;
  currentRun.survivalSeconds = Math.round(t * 10) / 10; // game-time (already scale-aware)
  currentRun.finalFloor = state.wave ?? 1;
  currentRun.finalLevel = state.player.level ?? 1;
  currentRun.totalKills = state.killCount ?? 0;
  currentRun.totalGoldEarned = (state.player.gold ?? 0) + goldSpentThisRun;
  currentRun.totalGoldSpent = goldSpentThisRun;
  currentRun.chestsOpened = state.chestsOpenedThisRun ?? 0;
  currentRun.rerollsUsed = rerollsUsedThisRun;
  currentRun.totalHealingReceived = healingThisRun;
  currentRun.deathPosition = { x: Math.round(state.player.x), y: Math.round(state.player.y) };

  // ── BETA 14.61 — CSV converge: snapshot all engine-state trackers onto
  // the run record. Reads from weaponLab module-level state (where the
  // engine-side accumulators land) so the bot CSV inherits everything WL
  // CSV has had since BETA 14.30+. Top weapon summaries computed from the
  // matrix here (not on-emit) since the run record is finalized first.
  try {
    const matrix = getLocalWeaponEnemyMatrix();
    currentRun.weaponEnemyMatrixJson = JSON.stringify(matrix);
    const top = computeTopWeaponSummary();
    currentRun.topWeaponVsZombie = top.topVsZombie;
    currentRun.topWeaponVsTimmy = top.topVsTimmy;
    currentRun.topWeaponVsCaseoh = top.topVsCaseoh;
    currentRun.topWeaponVsElite = top.topVsElite;
    currentRun.weakestWeaponOverall = top.weakest;
    currentRun.percTrackerJson = JSON.stringify(getLocalPercTracker());
    currentRun.itemTrackerJson = JSON.stringify(getLocalItemTracker());
    currentRun.shrineTrackerJson = JSON.stringify(getLocalShrineTracker());
    currentRun.floorEffectTrackerJson = JSON.stringify(getLocalFloorEffectTracker());
    currentRun.synergyTrackerJson = JSON.stringify(getLocalSynergyTracker());
    currentRun.eventLogJson = JSON.stringify(getLocalEventLog());
    currentRun.deathContextJson = JSON.stringify(getLocalDeathContext());
    currentRun.nearMissEventsLogJson = JSON.stringify(getLocalNearMissEvents());
    currentRun.retreatEventsLogJson = JSON.stringify(getLocalRetreatEvents());
    // Phase 1 event counters from state
    currentRun.reviveEventsThisRun = state._reviveEventsThisRun ?? 0;
    currentRun.healEventsThisRun = state._healEventsThisRun ?? 0;
    currentRun.dodgeEventsThisRun = state._dodgeEventsThisRun ?? 0;
    currentRun.phaseEventsThisRun = state._phaseEventsThisRun ?? 0;
    currentRun.knockbackEventsThisRun = state._knockbackEventsThisRun ?? 0;
    currentRun.enemyAttacksAttemptedThisRun = state._enemyAttacksAttemptedThisRun ?? 0;
    currentRun.enemyAttacksHitThisRun = state._enemyAttacksHitThisRun ?? 0;
    currentRun.enemyAttacksMissedByEvasion = state._enemyAttacksMissedByEvasion ?? 0;
    currentRun.enemyAttacksMissedByIframe = state._enemyAttacksMissedByIframe ?? 0;
    currentRun.enemyAttacksMissedByPhase = state._enemyAttacksMissedByPhase ?? 0;
    currentRun.firstBloodTime = state._firstBloodTime ?? 0;
    currentRun.firstDamageTakenTime = state._firstDamageTakenTime ?? 0;
    currentRun.longestStretchBetweenHitsMs = state._longestStretchBetweenHitsMs ?? 0;
    currentRun.engineFramePerfJson = JSON.stringify(state._engineFramePerfThisRun ?? {});
    // BETA 14.72 — Bot diagnostics snapshot
    currentRun.botPositionTrailJson = JSON.stringify(getLocalBotPositionTrail());
    currentRun.stuckEventsJson = JSON.stringify(getLocalStuckEvents());
    currentRun.botMapCellVisitsJson = JSON.stringify(getLocalBotMapCellVisits());
    currentRun.decisionTraceJson = JSON.stringify(getLocalDecisionTrace());
    // Stat snapshots — read from player live state (final values)
    currentRun.finalDamagePct = state.player.damageBonus ?? 0;
    currentRun.finalCritChancePct = state.player.critChance ?? 0;
    currentRun.finalAttackSpeedPct = state.player.attackSpeedBonus ?? 0;
    currentRun.finalMaxHP = state.player.maxHp ?? 0;
  } catch (_e) {
    // BETA 14.61 — guard against any missing tracker — bot CSV still emits
    // even if a single tracker fails. Empty fields show as undefined.
  }

  // ── Determine death cause ──
  // Find the nearest enemy to the player at death
  const enemies = state.enemies ?? [];
  if (enemies.length > 0) {
    let nearest: Enemy | null = null;
    let nearestDist = Infinity;
    for (const e of enemies) {
      if (!e.alive) continue;
      const dx = e.x - state.player.x;
      const dy = e.y - state.player.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = e;
      }
    }
    if (nearest && nearestDist < 200) {
      currentRun.deathCause = `enemy:${nearest.type}`;
      currentRun.deathEnemyType = nearest.type;
    } else {
      currentRun.deathCause = 'unknown (no nearby enemy)';
      currentRun.deathEnemyType = 'none';
    }
  }

  // ── Capture final stats ──
  const p = state.player;
  currentRun.finalStats = {
    maxHp: p.maxHp ?? 100,
    damage: p.damageBonus ?? 0,          // Player uses damageBonus, not damage
    armor: p.armor ?? 0,
    moveSpeed: p.speed ?? 200,            // Player uses speed, not moveSpeed
    attackSpeed: p.attackSpeedBonus ?? 0, // Player uses attackSpeedBonus
    critChance: p.critChance ?? 5,
    critDamage: p.critDamage ?? 150,
    lifesteal: p.lifesteal ?? 0,
    hpRegen: p.hpRegen ?? 0,
    evasion: p.evasion ?? 0,
    knockback: p.knockbackBonus ?? 0,    // Player uses knockbackBonus
    xpMultiplier: p.xpMultiplier ?? 1,
    goldMultiplier: p.goldMultiplier ?? 1,
    pickupRange: p.pickupRange ?? 40,
    luck: p.luck ?? 0,
    thorns: p.thorns ?? 0,
    shield: p.shield ?? 0,
  };

  // ── Capture final weapon data ──
  snapshotWeapons(state, t);

  // ── Capture final percs ──
  const percContribs = getPercContributions();
  currentRun.finalPercs = percContribs.map(pc => ({
    percName: pc.percName,
    stat: pc.stat,
    level: pc.level,
    totalBonus: pc.accumulatedBonus,
  }));

  // ── Capture shrine buffs ──
  currentRun.shrineBuffs = getAppliedBuffsList().map(b => ({
    stat: b.stat, value: b.value, label: b.label,
  }));

  // ── Balance report ──
  try {
    const report = computeBalanceReport(state);
    currentRun.balanceScore = report.balanceScore;
    currentRun.balanceLabel = report.balanceLabel;
    currentRun.avgPlayerTTK = report.avgPlayerTTK;
    currentRun.avgEnemyTTK = report.avgEnemyTTK;
    currentRun.killsPerMinute = report.killsPerMinute;
  } catch { /* balance report may fail if state is weird at death */ }

  // ── Wave / enemy density summary ──
  const waveStateFinal = getWaveSystemState();
  currentRun.totalSpawned   = waveStateFinal?.totalSpawned ?? 0;
  currentRun.bossesSpawned  = waveStateFinal?.bossesSpawned ?? 0;
  currentRun.totalSwarms    = waveStateFinal?.totalSwarms ?? 0;
  currentRun.finalWave      = state.wave ?? 1;
  currentRun.avgEnemyCount  = _enemyCountSamples > 0 ? Math.round(_enemyCountAccum / _enemyCountSamples) : 0;

  // ── Weapon damage totals ──
  const ws = getWeaponSystemState();
  if (ws) {
    currentRun.totalDamageDealt = ws.totalDamageDealt;
  }

  // ── Store run ──
  allRuns.push(currentRun);
  printRunSummary(currentRun);
  currentRun = null;

  // ── Auto-save to localStorage after every run ──
  saveRunsToStorage();

  // ── Auto-download disabled (was firing on every run after MAX_RUNS threshold).
  //    Use the on-screen "📊 BOT CSV" button to download whenever you want.
  //    Continuous training intentionally accumulates runs past 100. ──
}

// ═══════════════════════════════════════════════════════════════════════════════
//  SECTION 5: CHOICE HANDLING — Level Up, Chest, Shrine
// ═══════════════════════════════════════════════════════════════════════════════

function handleLevelUpChoice(state: GameState): void {
  if (!isPendingLevelUp()) return;

  const options = getLevelUpOptionDisplayData();
  if (!options || options.length === 0) return;

  // ── Record what was offered ──
  const offered = options.map((opt, i) => ({
    index: i,
    name: opt.percName || opt.weaponId || 'unknown',
    id: opt.weaponId || opt.percName || 'unknown',
    rarity: opt.rarity,
    type: opt.type,
    description: opt.percDescription || opt.bonusDisplay || '',
  }));

  // ── Bot decision logic ──
  const { pickIndex, reason } = decideLevelUpPick(options, state);

  // ── Execute the pick ──
  const success = handleLevelUpSelection(pickIndex);
  if (!success) return;

  // ── Record the choice ──
  if (currentRun) {
    currentRun.choices.push({
      timestamp: state.time,
      type: 'levelup',
      offered,
      pickedIndex: pickIndex,
      pickedName: offered[pickIndex]?.name ?? 'unknown',
      reason,
      rerolled: false,
    });

    // Track as item acquired
    const picked = options[pickIndex];
    if (picked) {
      currentRun.items.push({
        itemId: picked.weaponId || picked.percName || 'unknown',
        itemName: picked.percName || picked.weaponId || 'unknown',
        rarity: picked.rarity,
        acquiredAt: state.time,
        source: 'levelup',
        stacks: 1,
      });
    }
  }

  choiceDebounce = 0.5; // half second cooldown
}

function handleChestChoice(state: GameState): void {
  const options = getChestOptionDisplayData();
  if (!options || options.length === 0) return;

  // ── Record what was offered ──
  const offered = options.map((opt, i) => ({
    index: i,
    name: opt.name,
    id: opt.itemId,
    rarity: opt.rarity,
    type: 'item',
    description: opt.description,
  }));

  // ── Bot decision: pick highest rarity, then first ──
  const rarityOrder = ['legendary', 'epic', 'rare', 'uncommon', 'common'];
  let pickIndex = 0;
  let bestRarityRank = 999;
  for (let i = 0; i < options.length; i++) {
    const rank = rarityOrder.indexOf(options[i].rarity);
    if (rank >= 0 && rank < bestRarityRank) {
      bestRarityRank = rank;
      pickIndex = i;
    }
  }

  const reason = `Picked "${options[pickIndex].name}" (${options[pickIndex].rarity}) — highest rarity available`;

  // ── Execute the pick ──
  const result = handleChestItemSelection(pickIndex);
  if (!result) return;

  // ── Record ──
  if (currentRun) {
    currentRun.choices.push({
      timestamp: state.time,
      type: 'chest',
      offered,
      pickedIndex: pickIndex,
      pickedName: options[pickIndex].name,
      reason,
      rerolled: false,
    });

    currentRun.items.push({
      itemId: options[pickIndex].itemId,
      itemName: options[pickIndex].name,
      rarity: options[pickIndex].rarity,
      acquiredAt: state.time,
      source: 'chest',
      stacks: 1,
    });
  }

  choiceDebounce = 0.5;
}

function handleShrineChoice(state: GameState): void {
  if (!isPendingShrineChoice()) return;

  // getShrineBuffDisplayOptions may not exist — fall back to picking index 0
  let options: any[] = [];
  try {
    const fn = (getShrineBuffDisplayOptions as any);
    if (typeof fn === 'function') options = fn();
  } catch { /* fallback */ }

  // Build offered list
  const offered = options.map((opt: any, i: number) => ({
    index: i,
    name: opt?.label || opt?.name || `Option ${i}`,
    id: opt?.id || `shrine_${i}`,
    rarity: opt?.rarity || 'common',
    type: 'buff',
    description: opt?.label || '',
  }));

  // Pick the first non-negative option
  let pickIndex = 0;
  for (let i = 0; i < options.length; i++) {
    if (options[i]?.rarity !== 'negative') {
      pickIndex = i;
      break;
    }
  }

  const reason = offered.length > 0
    ? `Picked "${offered[pickIndex]?.name}" — first non-negative buff`
    : 'No options visible, picked index 0';

  const result = handleShrineSelection(pickIndex);

  if (currentRun) {
    currentRun.choices.push({
      timestamp: state.time,
      type: 'shrine',
      offered: offered.length > 0 ? offered : [{ index: 0, name: 'unknown', id: 'unknown', rarity: 'unknown', type: 'buff', description: '' }],
      pickedIndex: pickIndex,
      pickedName: offered[pickIndex]?.name ?? 'unknown',
      reason,
      rerolled: false,
    });
    currentRun.shrinesUsed++;
  }

  choiceDebounce = 0.5;
}

function handleUltraChestChoice(state: GameState): void {
  // Ultra chests auto-give — just record that it happened
  // The phase will transition back to playing automatically
  choiceDebounce = 1.0;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  SECTION 6: BOT DECISION LOGIC — Smart picking
// ═══════════════════════════════════════════════════════════════════════════════

/** Priority scoring for level-up picks */
function decideLevelUpPick(options: LevelUpOptionDisplay[], state: GameState): { pickIndex: number; reason: string } {
  const p = state.player;
  const hpPct = p.hp / (p.maxHp || 100);

  // Score each option
  const scores: { index: number; score: number; reason: string }[] = options.map((opt, i) => {
    let score = 0;
    let reason = '';

    // ── Rarity bonus ──
    const rarityScore: Record<string, number> = { common: 1, uncommon: 2, rare: 4, epic: 8, legendary: 16 };
    score += rarityScore[opt.rarity] ?? 1;

    // ── New weapon bonus (always valuable) ──
    if (opt.type === 'new_weapon') {
      score += 15;
      reason = 'New weapon unlock';
    }

    // ── Weapon upgrade bonus ──
    if (opt.type === 'weapon_upgrade') {
      score += 10;
      reason = 'Weapon upgrade';
    }

    // ── Stat-based scoring ──
    // If low HP, prioritize healing/defense
    if (hpPct < 0.3) {
      if (opt.percDescription?.toLowerCase().includes('hp') || opt.percDescription?.toLowerCase().includes('heal') || opt.percDescription?.toLowerCase().includes('regen')) {
        score += 12;
        reason = 'Low HP — prioritizing survival';
      }
      if (opt.percDescription?.toLowerCase().includes('armor') || opt.percDescription?.toLowerCase().includes('shield')) {
        score += 8;
        reason = 'Low HP — prioritizing defense';
      }
    }

    // Damage is always good
    if (opt.percDescription?.toLowerCase().includes('damage') || opt.percDescription?.toLowerCase().includes('crit')) {
      score += 6;
      if (!reason) reason = 'Damage boost';
    }

    // Attack speed is great
    if (opt.percDescription?.toLowerCase().includes('attack speed')) {
      score += 5;
      if (!reason) reason = 'Attack speed boost';
    }

    // Movement speed helps survival
    if (opt.percDescription?.toLowerCase().includes('movement') || opt.percDescription?.toLowerCase().includes('speed')) {
      score += 4;
      if (!reason) reason = 'Movement speed';
    }

    // Lifesteal is excellent
    if (opt.percDescription?.toLowerCase().includes('lifesteal')) {
      score += 7;
      if (!reason) reason = 'Lifesteal for sustain';
    }

    if (!reason) reason = `Score: ${score} (rarity: ${opt.rarity})`;

    return { index: i, score, reason };
  });

  // Pick highest score
  scores.sort((a, b) => b.score - a.score);
  return { pickIndex: scores[0].index, reason: scores[0].reason };
}

// ═══════════════════════════════════════════════════════════════════════════════
//  SECTION 7: MOVEMENT & AIMING
// ═══════════════════════════════════════════════════════════════════════════════

function updateMovement(state: GameState, dt: number): void {
  const p = state.player;
  const enemies = state.enemies ?? [];

  // Clear all keys first
  state.keys.delete('w'); state.keys.delete('a');
  state.keys.delete('s'); state.keys.delete('d');

  if (enemies.length === 0) {
    // No enemies — wander toward center or nearest chest/shrine
    wanderTowardInterest(state);
    return;
  }

  // Find nearest enemy
  let nearest: Enemy | null = null;
  let nearestDist = Infinity;
  for (const e of enemies) {
    if (!e.alive) continue;
    const dx = e.x - p.x;
    const dy = e.y - p.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < nearestDist) { nearestDist = dist; nearest = e; }
  }

  if (!nearest) return;

  const hpPct = p.hp / (p.maxHp || 100);

  // ── Flee if low HP and enemy is close ──
  if (hpPct < 0.25 && nearestDist < 300) {
    const dx = p.x - nearest.x;
    const dy = p.y - nearest.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    if (dx / len > 0.3) state.keys.add('d');
    if (dx / len < -0.3) state.keys.add('a');
    if (dy / len > 0.3) state.keys.add('s');
    if (dy / len < -0.3) state.keys.add('w');
    return;
  }

  // ── Kite: maintain ~250 distance from nearest enemy ──
  const idealDist = 250;
  if (nearestDist < idealDist - 50) {
    // Too close — back away
    const dx = p.x - nearest.x;
    const dy = p.y - nearest.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    if (dx / len > 0.3) state.keys.add('d');
    if (dx / len < -0.3) state.keys.add('a');
    if (dy / len > 0.3) state.keys.add('s');
    if (dy / len < -0.3) state.keys.add('w');
  } else if (nearestDist > idealDist + 100) {
    // Too far — close in
    const dx = nearest.x - p.x;
    const dy = nearest.y - p.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    if (dx / len > 0.3) state.keys.add('d');
    if (dx / len < -0.3) state.keys.add('a');
    if (dy / len > 0.3) state.keys.add('s');
    if (dy / len < -0.3) state.keys.add('w');
  } else {
    // Good distance — strafe perpendicular
    const dx = nearest.x - p.x;
    const dy = nearest.y - p.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    // Perpendicular vector
    const px = -dy / len;
    const py = dx / len;
    if (px > 0.3) state.keys.add('d');
    if (px < -0.3) state.keys.add('a');
    if (py > 0.3) state.keys.add('s');
    if (py < -0.3) state.keys.add('w');
  }
}

function wanderTowardInterest(state: GameState): void {
  // Move toward nearest goop box, chest, shrine, or portal
  const p = state.player;
  let targetX = state.map.WORLD_SIZE / 2;
  let targetY = state.map.WORLD_SIZE / 2;
  let bestDist = Infinity;

  // Check goop boxes
  for (const box of state.goopBoxes ?? []) {
    if (box.exploding) continue;
    const dx = box.x - p.x;
    const dy = box.y - p.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < bestDist) { bestDist = dist; targetX = box.x; targetY = box.y; }
  }

  // Check portal
  if (state.portal && state.portal.active) {
    const dx = state.portal.x - p.x;
    const dy = state.portal.y - p.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < bestDist) { bestDist = dist; targetX = state.portal.x; targetY = state.portal.y; }
  }

  const dx = targetX - p.x;
  const dy = targetY - p.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  if (len > 50) {
    if (dx / len > 0.3) state.keys.add('d');
    if (dx / len < -0.3) state.keys.add('a');
    if (dy / len > 0.3) state.keys.add('s');
    if (dy / len < -0.3) state.keys.add('w');
  }
}

function updateAiming(state: GameState): void {
  const enemies = state.enemies ?? [];
  if (enemies.length === 0) {
    state.mouse.down = false;
    return;
  }

  // Aim at nearest enemy
  let nearest: Enemy | null = null;
  let nearestDist = Infinity;
  for (const e of enemies) {
    if (!e.alive) continue;
    const dx = e.x - state.player.x;
    const dy = e.y - state.player.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < nearestDist) { nearestDist = dist; nearest = e; }
  }

  if (nearest) {
    state.mouse.worldX = nearest.x;
    state.mouse.worldY = nearest.y;
    state.mouse.down = true;
  }
}

function updateInteraction(state: GameState): void {
  // Press E when near interactable
  if (state.interactTarget) {
    state.keys.add('e');
  } else {
    state.keys.delete('e');
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  SECTION 8: DATA TRACKING (per frame)
// ═══════════════════════════════════════════════════════════════════════════════

function trackCombatData(state: GameState, dt: number): void {
  if (!currentRun) return;
  const t = state.time;

  // ── Track kills per enemy type ──
  const currentKills = state.killCount ?? 0;
  const newKills = currentKills - lastKillCount;
  if (newKills > 0) {
    // Determine which enemies died by comparing snapshots
    const currentEnemies = new Set((state.enemies ?? []).filter(e => e.alive).map(e => e.id));
    for (const [id, prev] of Array.from(prevEnemySnapshot)) {
      if (!currentEnemies.has(id)) {
        // This enemy died
        const etype = prev.type;
        if (!currentRun.enemyTypes[etype]) {
          currentRun.enemyTypes[etype] = {
            enemyType: etype,
            totalKilled: 0,
            totalDamageDealtToPlayer: 0,
            firstSeenAt: t,
            lastKilledAt: t,
            killsOverTime: [],
            observedMaxHp: 0,
            observedSpeed: 0,
            observedDamage: 0,
            firstSpawnAt: t,
            peakCount: 0,
          };
        }
        currentRun.enemyTypes[etype].totalKilled++;
        currentRun.enemyTypes[etype].lastKilledAt = t;
      }
    }
    lastKillCount = currentKills;
    // BETA 14.76 — staleness counter: any kill = bot is succeeding
    _lastKillTimeT = t;
  }

  // ── Snapshot current enemies for next frame comparison ──
  prevEnemySnapshot.clear();
  for (const e of state.enemies ?? []) {
    if (e.alive) {
      prevEnemySnapshot.set(e.id, { hp: e.hp, type: e.type });
      // Track first-seen stats per enemy type
      if (currentRun) {
        const etype = e.type;
        if (!currentRun.enemyTypes[etype]) {
          currentRun.enemyTypes[etype] = {
            enemyType: etype,
            totalKilled: 0,
            totalDamageDealtToPlayer: 0,
            firstSeenAt: t,
            lastKilledAt: 0,
            killsOverTime: [],
            observedMaxHp: e.maxHp,
            observedSpeed: e.speed,
            observedDamage: e.damage,
            firstSpawnAt: t,
            peakCount: 0,
          };
        }
        // Update peak count per type
        const typeAliveCount = (state.enemies ?? []).filter(x => x.alive && x.type === etype).length;
        if (typeAliveCount > currentRun.enemyTypes[etype].peakCount) {
          currentRun.enemyTypes[etype].peakCount = typeAliveCount;
        }
      }
    }
  }

  // ── Track damage taken ──
  const currentHp = state.player.hp;
  if (currentHp < lastHp) {
    const dmgTaken = lastHp - currentHp;
    currentRun.totalDamageTaken += dmgTaken;

    // Attribute to nearest enemy
    const enemies = state.enemies ?? [];
    let nearest: Enemy | null = null;
    let nearestDist = Infinity;
    for (const e of enemies) {
      if (!e.alive) continue;
      const dx = e.x - state.player.x;
      const dy = e.y - state.player.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < nearestDist) { nearestDist = dist; nearest = e; }
    }
    if (nearest && nearestDist < 200) {
      const etype = nearest.type;
      if (!currentRun.enemyTypes[etype]) {
        currentRun.enemyTypes[etype] = {
          enemyType: etype, totalKilled: 0, totalDamageDealtToPlayer: 0,
          firstSeenAt: t, lastKilledAt: 0, killsOverTime: [],
          observedMaxHp: nearest.maxHp, observedSpeed: nearest.speed, observedDamage: nearest.damage,
          firstSpawnAt: t, peakCount: 0,
        };
      }
      currentRun.enemyTypes[etype].totalDamageDealtToPlayer += dmgTaken;
    }
  } else if (currentHp > lastHp) {
    // Healing detected
    healingThisRun += (currentHp - lastHp);
  }
  lastHp = currentHp;

  // ── Track gold changes ──
  const currentGold = state.player.gold ?? 0;
  if (currentGold < lastGold) {
    goldSpentThisRun += (lastGold - currentGold);
  }
  lastGold = currentGold;
}

function trackWaveEvents(state: GameState): void {
  if (!currentRun) return;
  const t = state.time;
  const wave = state.wave ?? 1;
  const waveState = getWaveSystemState();
  // BETA 13.70 FPS: O(1) maintained counter (was filter walk over up to 200 enemies).
  const aliveCount = getEnemyCount();

  // Wave number change
  if (wave !== _lastWave) {
    currentRun.waveEvents.push({ t, event: 'wave_start', wave, enemyCount: aliveCount });
    currentRun.finalWave = wave;
    _lastWave = wave;
  }

  if (waveState) {
    // Boss spawn
    const bossNowActive = waveState.bossActive;
    if (bossNowActive && !_lastBossActive) {
      currentRun.waveEvents.push({ t, event: 'boss_spawn', wave, enemyCount: aliveCount });
    }
    if (!bossNowActive && _lastBossActive) {
      currentRun.waveEvents.push({ t, event: 'boss_killed', wave, enemyCount: aliveCount });
    }
    _lastBossActive = bossNowActive;

    // Swarm start/end
    const swarmNow = waveState.activeSwarm;
    if (swarmNow && !_lastSwarmActive) {
      currentRun.waveEvents.push({ t, event: 'swarm_start', wave, enemyCount: aliveCount });
    }
    if (!swarmNow && _lastSwarmActive) {
      currentRun.waveEvents.push({ t, event: 'swarm_end', wave, enemyCount: aliveCount });
    }
    _lastSwarmActive = swarmNow;

    // Floor advance
    if (waveState.currentFloor !== _lastFloor) {
      currentRun.waveEvents.push({ t, event: 'floor_advance', wave, enemyCount: aliveCount, detail: `floor_${waveState.currentFloor + 1}` });
      _lastFloor = waveState.currentFloor;
    }

    // Final swarm
    if (waveState.inFinalSwarm && !_lastFinalSwarm) {
      currentRun.waveEvents.push({ t, event: 'final_swarm', wave, enemyCount: aliveCount });
      _lastFinalSwarm = true;
    }
  }
}

function snapshotWeapons(state: GameState, t: number): void {
  if (!currentRun) return;
  const ws = getWeaponSystemState();
  if (!ws) return;

  // Update or create weapon entries
  for (const w of ws.weapons) {
    const def = getWeaponDef(w.definitionId);
    let existing = currentRun.weapons.find(wd => wd.weaponId === w.definitionId);
    if (!existing) {
      existing = {
        weaponId: w.definitionId,
        weaponName: def?.name ?? w.definitionId,
        acquiredAt: t,
        finalLevel: w.level,
        finalRarity: w.rarity,
        totalDamageDealt: 0,
        totalShotsFired: 0,
        totalShotsHit: 0,
        totalCrits: 0,
        timesUpgraded: 0,
        dpsOverTime: [],
      };
      currentRun.weapons.push(existing);
    }
    existing.finalLevel = w.level;
    existing.finalRarity = w.rarity;
    // Read per-weapon telemetry from WeaponInstance (added in weapons.ts)
    existing.totalDamageDealt = w.totalDamageDealt ?? 0;
    existing.totalShotsFired = w.totalShotsFired ?? 0;
    existing.totalShotsHit = (w as any).totalShotsHit ?? 0;
    existing.totalCrits = w.totalCrits ?? 0;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  SECTION 9: TIMELINE SAMPLING
// ═══════════════════════════════════════════════════════════════════════════════

function sampleTimeline(state: GameState): void {
  if (!currentRun) return;
  const ws = getWeaponSystemState();
  const t = state.time;
  const waveState = getWaveSystemState();
  const aliveEnemies = (state.enemies ?? []).filter(e => e.alive);
  const eCount = aliveEnemies.length;
  _enemyCountAccum += eCount;
  _enemyCountSamples++;
  if (currentRun && eCount > currentRun.peakEnemyCount) currentRun.peakEnemyCount = eCount;

  currentRun.timeline.push({
    t: Math.round(t),
    hp: Math.round(state.player.hp),
    maxHp: state.player.maxHp ?? 100,
    shield: state.player.shield ?? 0,
    gold: state.player.gold ?? 0,
    level: state.player.level ?? 1,
    kills: state.killCount ?? 0,
    dps: t > 0 ? Math.round((ws?.totalDamageDealt ?? 0) / t) : 0,
    enemyCount: eCount,
    phase: state.phase,
    wave: state.wave ?? 1,
    bossActive: waveState?.bossActive ?? false,
    swarmActive: waveState?.activeSwarm ?? false,
    totalSpawned: waveState?.totalSpawned ?? 0,
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
//  SECTION 10: ANOMALY DETECTION
// ═══════════════════════════════════════════════════════════════════════════════

function detectAnomalies(state: GameState): void {
  if (!currentRun) return;
  const t = state.time;
  const p = state.player;

  // ── HP overflow ──
  if (p.hp > (p.maxHp ?? 100) + (p.shield ?? 0) + 10) {
    currentRun.anomalies.push({
      timestamp: t, severity: 'bug', category: 'hp_overflow',
      message: `HP ${Math.round(p.hp)} exceeds maxHP ${p.maxHp} + shield ${p.shield ?? 0}`,
      data: { hp: p.hp, maxHp: p.maxHp, shield: p.shield },
    });
  }

  // ── Negative gold ──
  if ((p.gold ?? 0) < 0) {
    currentRun.anomalies.push({
      timestamp: t, severity: 'bug', category: 'negative_gold',
      message: `Gold is negative: ${p.gold}`,
    });
  }

  // ── Enemy out of bounds ──
  const worldSize = state.map.WORLD_SIZE;
  for (const e of state.enemies ?? []) {
    if (!e.alive) continue;
    if (e.x < -100 || e.y < -100 || e.x > worldSize + 100 || e.y > worldSize + 100) {
      currentRun.anomalies.push({
        timestamp: t, severity: 'warning', category: 'enemy_oob',
        message: `Enemy ${e.type}#${e.id} out of bounds at (${Math.round(e.x)}, ${Math.round(e.y)})`,
      });
    }
  }

  // ── Stuck detection (player hasn't moved in 5 seconds) ──
  if (currentRun.timeline.length >= 5) {
    const recent = currentRun.timeline.slice(-5);
    const allSamePos = recent.every(s =>
      Math.abs(s.hp - recent[0].hp) < 1 && s.kills === recent[0].kills
    );
    if (allSamePos && state.phase === 'playing' && (state.enemies ?? []).length > 0) {
      // Only flag once per 10 seconds
      const lastStuck = currentRun.anomalies.findLast(a => a.category === 'player_stuck');
      if (!lastStuck || t - lastStuck.timestamp > 10) {
        currentRun.anomalies.push({
          timestamp: t, severity: 'warning', category: 'player_stuck',
          message: `Player appears stuck — no kills or HP change for 5 seconds with enemies present`,
        });
        // BETA 14.72 — capture rich stuck-event context for bot diagnostics.
        // Categorize the cause so PSBOT RUN can recommend specific bot AI tweaks.
        // BETA 14.76 — major enrichment per PSBOT RUN cycle-10 finding (84% of
        // stuck events are bot-fault, not enemy-fault). Adds equipped weapons +
        // their actual lock-on ranges, closest enemy actually reachable by any
        // equipped weapon, bot velocity, top-N nearby enemies with angle, loot
        // orbs nearby, and seconds-since-last-kill / damage. The previous
        // categorization used blanket 100/200/300wu thresholds; now uses each
        // weapon's actual lockOnRange (spatula 65wu vs sniper 600wu = wildly
        // different "in range" semantics).
        try {
          const px = state.player.x;
          const py = state.player.y;
          // Top-K closest enemies (within 500wu, capped at 8 for CSV size)
          const allDetail: { type: string; dist: number; angleDeg: number }[] = [];
          const enemiesInRange: { type: string; dist: number }[] = [];
          let nearestDist = Infinity;
          for (const e of state.enemies ?? []) {
            if (!e.alive) continue;
            const dx = e.x - px;
            const dy = e.y - py;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d < 500) {
              const angleDeg = Math.round(Math.atan2(dy, dx) * 180 / Math.PI);
              allDetail.push({ type: e.type, dist: Math.round(d), angleDeg });
            }
            if (d < 200) enemiesInRange.push({ type: e.type, dist: Math.round(d) });
            if (d < nearestDist) nearestDist = d;
          }
          allDetail.sort((a, b) => a.dist - b.dist);
          const nearbyEnemiesDetail = allDetail.slice(0, 8);

          // Tally enemy types within 200wu (existing field)
          const enemyTypesNearby: Record<string, number> = {};
          for (const er of enemiesInRange) {
            enemyTypesNearby[er.type] = (enemyTypesNearby[er.type] ?? 0) + 1;
          }

          // BETA 14.76 — equipped weapons + their actual lock-on ranges
          const ws = getWeaponSystemState();
          const equippedWeapons: Array<{ id: string; level: number; lockOnRange: number }> = [];
          let maxLockOnRange = 0;
          if (ws?.weapons) {
            for (const w of ws.weapons) {
              const def = getWeaponDef(w.definitionId);
              const lor = def?.lockOnRange ?? 0;
              equippedWeapons.push({ id: w.definitionId, level: w.level ?? 1, lockOnRange: lor });
              if (lor > maxLockOnRange) maxLockOnRange = lor;
            }
          }

          // BETA 14.76 — closest enemy that's actually reachable by any equipped weapon
          let closestEnemyInLockOnRange: { type: string; dist: number; weaponId: string } | null = null;
          let closestReachableDist = Infinity;
          for (const e of state.enemies ?? []) {
            if (!e.alive) continue;
            const d = Math.sqrt((e.x - px) ** 2 + (e.y - py) ** 2);
            for (const ew of equippedWeapons) {
              if (d <= ew.lockOnRange && d < closestReachableDist) {
                closestReachableDist = d;
                closestEnemyInLockOnRange = { type: e.type, dist: Math.round(d), weaponId: ew.id };
              }
            }
          }

          // BETA 14.76 — bot velocity (was bot trying to move? Direction?)
          const pvx = (state.player as { vx?: number }).vx ?? 0;
          const pvy = (state.player as { vy?: number }).vy ?? 0;
          const pSpeed = Math.sqrt(pvx * pvx + pvy * pvy);
          const botVelocity = { vx: Math.round(pvx), vy: Math.round(pvy), speed: Math.round(pSpeed) };

          // BETA 14.76 — pickup orbs within 500wu (xp + gold + health)
          let pickupOrbsNearby = 0;
          try {
            const orbs = (state.lootOrbs as { orbs?: Array<{ x: number; y: number }> } | undefined)?.orbs;
            if (orbs) {
              for (const o of orbs) {
                const od = Math.sqrt((o.x - px) ** 2 + (o.y - py) ** 2);
                if (od < 500) pickupOrbsNearby++;
              }
            }
          } catch (_e) { /* ignore */ }

          // BETA 14.76 — staleness counters: seconds since last kill / damage dealt
          const secondsSinceLastKill = _lastKillTimeT > 0 ? Math.round((t - _lastKillTimeT) * 10) / 10 : -1;
          const secondsSinceLastDamageDealt = _lastDamageDealtTimeT > 0 ? Math.round((t - _lastDamageDealtTimeT) * 10) / 10 : -1;

          // Heuristic categorization (BETA 14.76 — uses ACTUAL maxLockOnRange,
          // not blanket 100/300wu. With equipped weapons known, "in range" is
          // weapon-specific; with no enemy in any equipped weapon's range,
          // it's truly weapon_oor or no_target.)
          const hpFrac = state.player.maxHp > 0 ? state.player.hp / state.player.maxHp : 0;
          let category: 'enemy_ring' | 'wall_block' | 'iframe' | 'no_target' | 'weapon_oor' | 'other' = 'other';
          if ((state.player.invincibleTimer ?? 0) > 0) category = 'iframe';
          else if ((state.enemies ?? []).filter(e => e.alive).length === 0) category = 'no_target';
          else if (closestEnemyInLockOnRange === null && equippedWeapons.length > 0) {
            // Enemies exist but none are within ANY equipped weapon's range
            category = 'weapon_oor';
          }
          else if (enemiesInRange.length >= 5) category = 'enemy_ring';
          // Wall-block proxy: nearest is very close (<60wu) but no kills happening
          else if (nearestDist < 60 && recent[0].kills === recent[recent.length - 1].kills) category = 'wall_block';

          // hasWeaponInRange: now uses actual maxLockOnRange instead of blanket 300wu
          const hasWeaponInRange = nearestDist < (maxLockOnRange || 300);
          // hasLoSToNearestEnemy: simplified — true if nearest is < 400wu (treats
          // open-line-of-sight as default; real raycast would need flowField).
          const hasLoSToNearestEnemy = nearestDist < 400;

          trackStuckEvent({
            t,
            x: Math.round(state.player.x), y: Math.round(state.player.y),
            hp: state.player.hp, hpFrac,
            level: state.player.level ?? 1,
            kills: state.killCount ?? 0,
            enemyCount: enemiesInRange.length,
            enemyTypesNearby,
            hasWeaponInRange,
            hasLoSToNearestEnemy,
            nearestEnemyDist: Math.round(nearestDist === Infinity ? 9999 : nearestDist),
            category,
            // BETA 14.76 — bot-fault diagnostic enrichment
            equippedWeapons,
            closestEnemyInLockOnRange,
            botVelocity,
            nearbyEnemiesDetail,
            pickupOrbsNearby,
            secondsSinceLastKill,
            secondsSinceLastDamageDealt,
          });
        } catch (_e) { /* never let diagnostics crash gameplay */ }
      }
    }
  }
  // BETA 14.72 — Position trail + map cell visits (per-frame; tracker
  // functions self-gate to avoid bloat).
  try {
    trackBotPosition(t, state.player.x, state.player.y, state.player.hp,
      state.player.level ?? 1, state.killCount ?? 0, (state.enemies ?? []).length);
    trackBotMapCell(t, state.player.x, state.player.y);
  } catch (_e) { /* diagnostics never crash gameplay */ }

  // ── Weapon system not dealing damage ──
  const ws = getWeaponSystemState();
  // BETA 14.76 — staleness counter: any damage dealt = bot is engaging successfully
  if (ws && ws.totalDamageDealt > lastDamageDealt) {
    lastDamageDealt = ws.totalDamageDealt;
    _lastDamageDealtTimeT = t;
  }
  if (ws && ws.totalShotsFired > 50 && ws.totalDamageDealt === 0) {
    const lastNoDmg = currentRun.anomalies.findLast(a => a.category === 'zero_damage');
    if (!lastNoDmg || t - lastNoDmg.timestamp > 15) {
      currentRun.anomalies.push({
        timestamp: t, severity: 'bug', category: 'zero_damage',
        message: `${ws.totalShotsFired} shots fired but 0 total damage dealt`,
      });
    }
  }

  // ── Enemy spawn flood (>80 enemies at once) — BETA 13.70 FPS: O(1) counter ──
  const aliveEnemies = getEnemyCount();
  if (aliveEnemies > 80) {
    const lastFlood = currentRun.anomalies.findLast(a => a.category === 'spawn_flood');
    if (!lastFlood || t - lastFlood.timestamp > 10) {
      currentRun.anomalies.push({
        timestamp: t, severity: 'warning', category: 'spawn_flood',
        message: `${aliveEnemies} enemies alive — potential spawn flood`,
      });
    }
  }
}

// ─── Passive choice observation helpers ──────────────────────────────────────

/**
 * Snapshot what options are offered on a choice screen WITHOUT making a pick.
 * v1's updateBot runs right after and actually selects the card.
 */
function observeChoiceScreens(state: GameState): void {
  if (state.phase === 'levelup' && pendingOfferType !== 'levelup') {
    const opts = getLevelUpOptionDisplayData();
    if (opts && opts.length > 0) {
      pendingOfferType = 'levelup';
      pendingOfferTimestamp = state.time;
      pendingOfferedOptions = opts.map((opt, i) => ({
        index: i,
        name: opt.percName || opt.weaponId || 'unknown',
        id: opt.weaponId || opt.percName || 'unknown',
        rarity: opt.rarity,
        type: opt.type,
        description: opt.percDescription || opt.bonusDisplay || '',
      }));
    }
  } else if (state.phase === 'chest_choice' && pendingOfferType !== 'chest') {
    const opts = getChestOptionDisplayData();
    if (opts && opts.length > 0) {
      pendingOfferType = 'chest';
      pendingOfferTimestamp = state.time;
      pendingOfferedOptions = opts.map((opt, i) => ({
        index: i,
        name: opt.name,
        id: opt.itemId,
        rarity: opt.rarity,
        type: 'item',
        description: opt.description,
      }));
    }
  } else if (state.phase === 'shrine_choice' && pendingOfferType !== 'shrine') {
    try {
      const opts = getShrineBuffDisplayOptions() as any[];
      if (opts && opts.length > 0) {
        pendingOfferType = 'shrine';
        pendingOfferTimestamp = state.time;
        pendingOfferedOptions = opts.map((opt: any, i: number) => ({
          index: i,
          name: opt?.label || opt?.name || `Option ${i}`,
          id: opt?.id || `shrine_${i}`,
          rarity: opt?.rarity || 'common',
          type: 'buff',
          description: opt?.label || '',
        }));
      }
    } catch { /* shrine options may not exist yet */ }
  }
}

/**
 * When a choice screen closes (phase leaves levelup/chest/shrine), record
 * what was offered. v1 already made the pick so we just note it was chosen.
 */
function finalizeObservedChoice(state: GameState): void {
  if (!pendingOfferType || !currentRun || pendingOfferedOptions.length === 0) {
    pendingOfferType = null;
    pendingOfferedOptions = [];
    return;
  }

  // ── v1 Pick Bridge: get the actual pick from v1 bot ──
  const v1Pick = getLastV1Pick();
  const pickedIdx = v1Pick?.pickedIndex ?? -1;
  const pickedName = v1Pick?.pickedName ?? '(v1 brain pick)';
  const pickReason = v1Pick?.reason ?? 'selected by autoPlaytester v1 brain';

  currentRun.choices.push({
    timestamp: pendingOfferTimestamp,
    type: pendingOfferType,
    offered: pendingOfferedOptions,
    pickedIndex: pickedIdx,
    pickedName: pickedName,
    reason: pickReason,
    rerolled: false,
  });

  // Record as acquired item — use v1's actual pick if available
  const actualPicked = pickedIdx >= 0 && pickedIdx < pendingOfferedOptions.length
    ? pendingOfferedOptions[pickedIdx]
    : pendingOfferedOptions[0]; // fallback to first
  currentRun.items.push({
    itemId: actualPicked.id,
    itemName: actualPicked.name,
    rarity: actualPicked.rarity,
    acquiredAt: pendingOfferTimestamp,
    source: pendingOfferType === 'levelup' ? 'levelup'
           : pendingOfferType === 'chest'  ? 'chest'
           : 'shrine',
    stacks: 1,
  });

  pendingOfferType = null;
  pendingOfferedOptions = [];
}

// ─── Phase transition ─────────────────────────────────────────────────────────

function handlePhaseTransition(from: string, to: string, state: GameState): void {
  // Finalize any pending choice observation (v1 already made the pick)
  const choicePhases = new Set(['levelup', 'chest_choice', 'shrine_choice', 'chest_spin', 'ultra_chest']);
  if (choicePhases.has(from) && !choicePhases.has(to)) {
    finalizeObservedChoice(state);
  }

  // Log phase transitions for debugging
  if (currentRun) {
    currentRun.anomalies.push({
      timestamp: state.time, severity: 'info', category: 'phase_change',
      message: `Phase: ${from} → ${to}`,
    });
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  SECTION 11: REPORTING
// ═══════════════════════════════════════════════════════════════════════════════

function printRunSummary(run: RunData): void {
  const divider = '═'.repeat(60);
  console.log(`\n%c${divider}`, 'color:#39ff14');
  console.log(`%c  RUN #${run.runNumber} COMPLETE — ${run.characterName}`, 'color:#00d4ff;font-size:14px;font-weight:bold');
  console.log(`%c${divider}`, 'color:#39ff14');

  console.log(`%c  Survived: ${run.survivalSeconds}s | Floor: ${run.finalFloor} | Level: ${run.finalLevel}`, 'color:#fff');
  console.log(`%c  Kills: ${run.totalKills} | KPM: ${run.killsPerMinute.toFixed(1)} | Balance: ${run.balanceLabel} (${run.balanceScore})`, 'color:#fff');
  console.log(`%c  Gold: +${run.totalGoldEarned} / -${run.totalGoldSpent} | Chests: ${run.chestsOpened} | Shrines: ${run.shrinesUsed}`, 'color:#fff');
  console.log(`%c  Damage Dealt: ${Math.round(run.totalDamageDealt)} | Damage Taken: ${Math.round(run.totalDamageTaken)} | Healing: ${Math.round(run.totalHealingReceived)}`, 'color:#fff');
  console.log(`%c  Death: ${run.deathCause} at (${run.deathPosition.x}, ${run.deathPosition.y})`, 'color:#ff4444');

  // Choices summary
  console.log(`\n%c  CHOICES (${run.choices.length} total):`, 'color:#ffcc00;font-weight:bold');
  for (const c of run.choices) {
    const offeredNames = c.offered.map(o => `${o.name}(${o.rarity})`).join(', ');
    console.log(`%c    [${c.timestamp.toFixed(0)}s] ${c.type}: offered [${offeredNames}] → picked "${c.pickedName}" — ${c.reason}`, 'color:#aaa');
  }

  // Weapons
  console.log(`\n%c  WEAPONS:`, 'color:#ff8800;font-weight:bold');
  for (const w of run.weapons) {
    console.log(`%c    ${w.weaponName} — Lv${w.finalLevel} (${w.finalRarity}) | Acquired: ${w.acquiredAt.toFixed(0)}s`, 'color:#aaa');
  }

  // Enemy breakdown
  console.log(`\n%c  ENEMIES:`, 'color:#ff4444;font-weight:bold');
  for (const [type, data] of Object.entries(run.enemyTypes)) {
    console.log(`%c    ${type}: ${data.totalKilled} killed | ${Math.round(data.totalDamageDealtToPlayer)} dmg to player`, 'color:#aaa');
  }

  // Items
  console.log(`\n%c  ITEMS (${run.items.length}):`, 'color:#4dff4d;font-weight:bold');
  for (const item of run.items) {
    console.log(`%c    [${item.acquiredAt.toFixed(0)}s] ${item.itemName} (${item.rarity}) from ${item.source}`, 'color:#aaa');
  }

  // Anomalies
  const bugs = run.anomalies.filter(a => a.severity === 'bug');
  const warnings = run.anomalies.filter(a => a.severity === 'warning');
  if (bugs.length > 0 || warnings.length > 0) {
    console.log(`\n%c  ANOMALIES: ${bugs.length} bugs, ${warnings.length} warnings`, 'color:#ff0000;font-weight:bold');
    for (const a of [...bugs, ...warnings]) {
      const color = a.severity === 'bug' ? '#ff0000' : '#ffaa00';
      console.log(`%c    [${a.timestamp.toFixed(0)}s] ${a.severity.toUpperCase()}: ${a.message}`, `color:${color}`);
    }
  }

  // Final stats
  console.log(`\n%c  FINAL STATS:`, 'color:#00ffcc;font-weight:bold');
  const s = run.finalStats;
  console.log(`%c    HP:${s.maxHp} DMG:${s.damage} ARM:${s.armor} SPD:${s.moveSpeed} ATK:${s.attackSpeed.toFixed(1)} CRIT:${s.critChance}%`, 'color:#aaa');
  console.log(`%c    LS:${s.lifesteal}% REGEN:${s.hpRegen} EVA:${s.evasion}% KB:${s.knockback} XP:${s.xpMultiplier.toFixed(1)}x GOLD:${s.goldMultiplier.toFixed(1)}x`, 'color:#aaa');

  console.log(`%c${divider}\n`, 'color:#39ff14');
}

/** Print cross-run analytics to console */
export function printReport(): void {
  if (allRuns.length === 0) {
    console.log('%c[BOT] No runs completed yet.', 'color:#ff4444');
    return;
  }

  const analytics = computeCrossRunAnalytics();
  const divider = '═'.repeat(70);

  console.log(`\n%c${divider}`, 'color:#39ff14');
  console.log(`%c  CROSS-RUN ANALYTICS — ${analytics.totalRuns} RUNS`, 'color:#00d4ff;font-size:16px;font-weight:bold');
  console.log(`%c${divider}`, 'color:#39ff14');

  console.log(`\n%c  AVERAGES:`, 'color:#ffcc00;font-weight:bold');
  console.log(`%c    Survival: ${analytics.avgSurvival.toFixed(1)}s | Kills: ${analytics.avgKills.toFixed(0)} | Level: ${analytics.avgLevel.toFixed(1)} | Floor: ${analytics.avgFloor.toFixed(1)}`, 'color:#fff');
  console.log(`%c    Gold: ${analytics.avgGold.toFixed(0)} | DmgDealt: ${analytics.avgDamageDealt.toFixed(0)} | DmgTaken: ${analytics.avgDamageTaken.toFixed(0)} | KPM: ${analytics.avgKillsPerMinute.toFixed(1)}`, 'color:#fff');

  console.log(`\n%c  BEST RUN: #${analytics.bestRun.runNumber} — ${analytics.bestRun.survivalSeconds}s, ${analytics.bestRun.kills} kills, Lv${analytics.bestRun.level}`, 'color:#39ff14');
  console.log(`%c  WORST RUN: #${analytics.worstRun.runNumber} — ${analytics.worstRun.survivalSeconds}s, ${analytics.worstRun.kills} kills, Lv${analytics.worstRun.level}`, 'color:#ff4444');

  // Death causes
  console.log(`\n%c  DEATH CAUSES:`, 'color:#ff4444;font-weight:bold');
  const sortedDeaths = Object.entries(analytics.deathCauses).sort((a, b) => b[1] - a[1]);
  for (const [cause, count] of sortedDeaths) {
    const pct = ((count / analytics.totalRuns) * 100).toFixed(0);
    console.log(`%c    ${cause}: ${count} (${pct}%)`, 'color:#aaa');
  }

  // Item pick rates
  console.log(`\n%c  ITEM PICK RATES (offered → picked):`, 'color:#4dff4d;font-weight:bold');
  const sortedItems = Object.entries(analytics.itemPickRates)
    .sort((a, b) => b[1].rate - a[1].rate)
    .slice(0, 15);
  for (const [name, data] of sortedItems) {
    console.log(`%c    ${name}: ${data.picked}/${data.offered} (${(data.rate * 100).toFixed(0)}%)`, 'color:#aaa');
  }

  // Trends
  console.log(`\n%c  SURVIVAL TREND: [${analytics.survivalTrend.map(s => s.toFixed(0)).join(', ')}]`, 'color:#00d4ff');
  console.log(`%c  KILLS TREND:    [${analytics.killsTrend.join(', ')}]`, 'color:#ff8800');
  console.log(`%c  LEVEL TREND:    [${analytics.levelTrend.join(', ')}]`, 'color:#ffcc00');

  // Anomalies
  console.log(`\n%c  ANOMALIES: ${analytics.totalAnomalies} total`, 'color:#ff0000;font-weight:bold');
  for (const [cat, count] of Object.entries(analytics.anomalyCategories)) {
    if (cat !== 'phase_change' && cat !== 'info') {
      console.log(`%c    ${cat}: ${count}`, 'color:#ffaa00');
    }
  }

  console.log(`%c${divider}\n`, 'color:#39ff14');
}

function computeCrossRunAnalytics(): CrossRunAnalytics {
  const runs = allRuns;
  const n = runs.length;

  const avg = (fn: (r: RunData) => number) => runs.reduce((s, r) => s + fn(r), 0) / n;

  // Item pick rates
  const itemPickRates: Record<string, { offered: number; picked: number; rate: number }> = {};
  for (const run of runs) {
    for (const choice of run.choices) {
      for (const opt of choice.offered) {
        if (!itemPickRates[opt.name]) itemPickRates[opt.name] = { offered: 0, picked: 0, rate: 0 };
        itemPickRates[opt.name].offered++;
        if (opt.index === choice.pickedIndex) {
          itemPickRates[opt.name].picked++;
        }
      }
    }
  }
  for (const data of Object.values(itemPickRates)) {
    data.rate = data.offered > 0 ? data.picked / data.offered : 0;
  }

  // Death causes
  const deathCauses: Record<string, number> = {};
  for (const run of runs) {
    deathCauses[run.deathCause] = (deathCauses[run.deathCause] ?? 0) + 1;
  }

  // Anomaly categories
  const anomalyCategories: Record<string, number> = {};
  let totalAnomalies = 0;
  for (const run of runs) {
    for (const a of run.anomalies) {
      anomalyCategories[a.category] = (anomalyCategories[a.category] ?? 0) + 1;
      totalAnomalies++;
    }
  }

  // Best/worst
  const sorted = [...runs].sort((a, b) => b.survivalSeconds - a.survivalSeconds);
  const best = sorted[0];
  const worst = sorted[sorted.length - 1];

  return {
    totalRuns: n,
    avgSurvival: avg(r => r.survivalSeconds),
    avgKills: avg(r => r.totalKills),
    avgLevel: avg(r => r.finalLevel),
    avgFloor: avg(r => r.finalFloor),
    avgGold: avg(r => r.totalGoldEarned),
    avgDamageDealt: avg(r => r.totalDamageDealt),
    avgDamageTaken: avg(r => r.totalDamageTaken),
    avgKillsPerMinute: avg(r => r.killsPerMinute),
    bestRun: { runNumber: best.runNumber, survivalSeconds: best.survivalSeconds, kills: best.totalKills, level: best.finalLevel },
    worstRun: { runNumber: worst.runNumber, survivalSeconds: worst.survivalSeconds, kills: worst.totalKills, level: worst.finalLevel },
    itemPickRates,
    weaponPickRates: {},  // TODO: separate weapon tracking
    deathCauses,
    survivalTrend: runs.map(r => r.survivalSeconds),
    killsTrend: runs.map(r => r.totalKills),
    levelTrend: runs.map(r => r.finalLevel),
    totalAnomalies,
    anomalyCategories,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
//  SECTION 12: CSV EXPORT — Full data dump
// ═══════════════════════════════════════════════════════════════════════════════

/** Download a comprehensive CSV with all run data */
export function downloadFullCSV(): void {
  if (allRuns.length === 0) {
    console.log('%c[BOT] No runs to export.', 'color:#ff4444');
    return;
  }

  // ── Sheet 1: Run Summary ──
  let csv = 'SHEET: RUN_SUMMARY\n';
  csv += 'run,gameVersion,botBrainVersion,timeScale,wallClockSec,character,survivalSec,floor,level,kills,kpm,goldEarned,goldSpent,dmgDealt,dmgTaken,healing,chests,shrines,rerolls,deathCause,deathEnemy,deathX,deathY,balanceScore,balanceLabel,avgPlayerTTK,avgEnemyTTK,maxHp,damage,armor,moveSpeed,attackSpeed,critChance,lifesteal,hpRegen,evasion,shield,xpMult,goldMult,numChoices,numItems,numWeapons,numAnomalies,numBugs,finalWave,peakEnemies,avgEnemies,totalSpawned,bossesSpawned,totalSwarms\n';
  for (const r of allRuns) {
    const s = r.finalStats;
    const bugs = r.anomalies.filter(a => a.severity === 'bug').length;
    csv += `${r.runNumber},${r.gameVersion ?? 'unknown'},${r.botBrainVersion ?? '2.x'},${r.timeScale ?? 1},${r.wallClockSec ?? 0},${r.characterName},${r.survivalSeconds},${r.finalFloor},${r.finalLevel},${r.totalKills},${r.killsPerMinute.toFixed(1)},${r.totalGoldEarned},${r.totalGoldSpent},${Math.round(r.totalDamageDealt)},${Math.round(r.totalDamageTaken)},${Math.round(r.totalHealingReceived)},${r.chestsOpened},${r.shrinesUsed},${r.rerollsUsed},${r.deathCause},${r.deathEnemyType},${r.deathPosition.x},${r.deathPosition.y},${r.balanceScore},${r.balanceLabel},${r.avgPlayerTTK.toFixed(1)},${r.avgEnemyTTK.toFixed(1)},${s.maxHp},${s.damage},${s.armor},${s.moveSpeed},${s.attackSpeed.toFixed(1)},${s.critChance},${s.lifesteal},${s.hpRegen},${s.evasion},${s.shield},${s.xpMultiplier.toFixed(1)},${s.goldMultiplier.toFixed(1)},${r.choices.length},${r.items.length},${r.weapons.length},${r.anomalies.length},${bugs},${r.finalWave ?? 1},${r.peakEnemyCount ?? 0},${r.avgEnemyCount ?? 0},${r.totalSpawned ?? 0},${r.bossesSpawned ?? 0},${r.totalSwarms ?? 0}\n`;
  }

  // ── Sheet 2: All Choices ──
  csv += '\nSHEET: ALL_CHOICES\n';
  csv += 'run,timestamp,type,offered_0_name,offered_0_rarity,offered_0_type,offered_1_name,offered_1_rarity,offered_1_type,offered_2_name,offered_2_rarity,offered_2_type,pickedIndex,pickedName,reason,rerolled\n';
  for (const r of allRuns) {
    for (const c of r.choices) {
      const o0 = c.offered[0] ?? { name: '', rarity: '', type: '' };
      const o1 = c.offered[1] ?? { name: '', rarity: '', type: '' };
      const o2 = c.offered[2] ?? { name: '', rarity: '', type: '' };
      csv += `${r.runNumber},${c.timestamp.toFixed(1)},${c.type},${o0.name},${o0.rarity},${o0.type},${o1.name},${o1.rarity},${o1.type},${o2.name},${o2.rarity},${o2.type},${c.pickedIndex},${c.pickedName},"${c.reason.replace(/"/g, "'")}",${c.rerolled}\n`;
    }
  }

  // ── Sheet 3: All Items Acquired ──
  csv += '\nSHEET: ALL_ITEMS\n';
  csv += 'run,timestamp,itemId,itemName,rarity,source,stacks\n';
  for (const r of allRuns) {
    for (const item of r.items) {
      csv += `${r.runNumber},${item.acquiredAt.toFixed(1)},${item.itemId},${item.itemName},${item.rarity},${item.source},${item.stacks}\n`;
    }
  }

  // ── Sheet 4: All Weapons ──
  csv += '\nSHEET: ALL_WEAPONS\n';
  csv += 'run,weaponId,weaponName,acquiredAt,finalLevel,finalRarity,totalDamage,totalShots,totalShotsHit,hitRatePct,totalCrits,timesUpgraded\n';
  for (const r of allRuns) {
    for (const w of r.weapons) {
      const _hitRate = w.totalShotsFired > 0 ? (100 * w.totalShotsHit / w.totalShotsFired).toFixed(1) : '0';
      csv += `${r.runNumber},${w.weaponId},${w.weaponName},${w.acquiredAt.toFixed(1)},${w.finalLevel},${w.finalRarity},${Math.round(w.totalDamageDealt)},${w.totalShotsFired},${w.totalShotsHit ?? 0},${_hitRate},${w.totalCrits},${w.timesUpgraded}\n`;
    }
  }

  // ── Sheet 5: Enemy Type Breakdown ──
  csv += '\nSHEET: ENEMY_TYPES\n';
  csv += 'run,enemyType,totalKilled,dmgToPlayer,firstSpawnAt,firstSeenAt,lastKilledAt,observedMaxHp,observedSpeed,observedDamage,peakCount\n';
  for (const r of allRuns) {
    for (const [type, data] of Object.entries(r.enemyTypes)) {
      csv += `${r.runNumber},${type},${data.totalKilled},${Math.round(data.totalDamageDealtToPlayer)},${data.firstSpawnAt?.toFixed(1) ?? data.firstSeenAt.toFixed(1)},${data.firstSeenAt.toFixed(1)},${data.lastKilledAt.toFixed(1)},${data.observedMaxHp ?? 0},${data.observedSpeed?.toFixed(1) ?? 0},${data.observedDamage?.toFixed(1) ?? 0},${data.peakCount ?? 0}\n`;
    }
  }

  // ── Sheet 6: Timeline (sampled every 1s) ──
  csv += '\nSHEET: TIMELINE\n';
  csv += 'run,t,hp,maxHp,shield,gold,level,kills,dps,enemyCount,phase,wave,bossActive,swarmActive,totalSpawned\n';
  for (const r of allRuns) {
    for (const s of r.timeline) {
      csv += `${r.runNumber},${s.t},${s.hp},${s.maxHp},${s.shield},${s.gold},${s.level},${s.kills},${s.dps},${s.enemyCount},${s.phase},${s.wave ?? 1},${s.bossActive ?? false},${s.swarmActive ?? false},${s.totalSpawned ?? 0}\n`;
    }
  }

  // ── Sheet 7: Anomalies ──
  csv += '\nSHEET: ANOMALIES\n';
  csv += 'run,timestamp,severity,category,message\n';
  for (const r of allRuns) {
    for (const a of r.anomalies) {
      if (a.category === 'phase_change') continue; // skip noise
      csv += `${r.runNumber},${a.timestamp.toFixed(1)},${a.severity},${a.category},"${a.message.replace(/"/g, "'")}"\n`;
    }
  }

  // ── Sheet 8: Final Percs ──
  csv += '\nSHEET: FINAL_PERCS\n';
  csv += 'run,percName,stat,level,totalBonus\n';
  for (const r of allRuns) {
    for (const pc of r.finalPercs) {
      csv += `${r.runNumber},${pc.percName},${pc.stat},${pc.level},${pc.totalBonus}\n`;
    }
  }

  // ── Sheet 9: Shrine Buffs ──
  csv += '\nSHEET: SHRINE_BUFFS\n';
  csv += 'run,stat,value,label\n';
  for (const r of allRuns) {
    for (const b of r.shrineBuffs) {
      csv += `${r.runNumber},${b.stat},${b.value},${b.label}\n`;
    }
  }

  // ── Sheet 10: Wave Events ──
  csv += '\nSHEET: WAVE_EVENTS\n';
  csv += 'run,t,event,wave,enemyCount,detail\n';
  for (const r of allRuns) {
    for (const e of (r.waveEvents ?? [])) {
      csv += `${r.runNumber},${e.t.toFixed(1)},${e.event},${e.wave},${e.enemyCount},${e.detail ?? ''}\n`;
    }
  }

  // ── BETA 14.61 — Sheet 11: Engine state trackers (CSV converge) ──
  // Closes the bot CSV gap PSBOT RUN flagged. Inherits ALL the per-mechanic
  // tracking that WeaponLab CSV has had since BETA 14.30+. Wide single-row-
  // per-run sheet so analyst can pivot/filter easily.
  csv += '\nSHEET: ENGINE_TRACKERS\n';
  csv += 'run,percTracker,itemTracker,shrineTracker,floorEffectTracker,weaponEnemyMatrix,synergyTracker,eventLog,deathContext,nearMissEventsLog,retreatEventsLog,topWeaponVsZombie,topWeaponVsTimmy,topWeaponVsCaseoh,topWeaponVsElite,weakestWeaponOverall,finalDamagePct,finalCritChancePct,finalAttackSpeedPct,finalMaxHP,reviveEvents,healEvents,dodgeEvents,phaseEvents,knockbackEvents,enemyAttacksAttempted,enemyAttacksHit,enemyAttacksMissedByEvasion,enemyAttacksMissedByIframe,enemyAttacksMissedByPhase,firstBloodTime,firstDamageTakenTime,longestStretchBetweenHitsMs,engineFramePerf,botPositionTrail,stuckEvents,botMapCellVisits,decisionTrace\n';
  // Helper: CSV-escape a JSON string (wrap in quotes, escape inner quotes).
  const _esc = (s: string | undefined) => `"${(s ?? '').replace(/"/g, '""')}"`;
  for (const r of allRuns) {
    csv += `${r.runNumber},${_esc(r.percTrackerJson)},${_esc(r.itemTrackerJson)},${_esc(r.shrineTrackerJson)},${_esc(r.floorEffectTrackerJson)},${_esc(r.weaponEnemyMatrixJson)},${_esc(r.synergyTrackerJson)},${_esc(r.eventLogJson)},${_esc(r.deathContextJson)},${_esc(r.nearMissEventsLogJson)},${_esc(r.retreatEventsLogJson)},${r.topWeaponVsZombie ?? ''},${r.topWeaponVsTimmy ?? ''},${r.topWeaponVsCaseoh ?? ''},${r.topWeaponVsElite ?? ''},${r.weakestWeaponOverall ?? ''},${r.finalDamagePct ?? 0},${r.finalCritChancePct ?? 0},${r.finalAttackSpeedPct ?? 0},${r.finalMaxHP ?? 0},${r.reviveEventsThisRun ?? 0},${r.healEventsThisRun ?? 0},${r.dodgeEventsThisRun ?? 0},${r.phaseEventsThisRun ?? 0},${r.knockbackEventsThisRun ?? 0},${r.enemyAttacksAttemptedThisRun ?? 0},${r.enemyAttacksHitThisRun ?? 0},${r.enemyAttacksMissedByEvasion ?? 0},${r.enemyAttacksMissedByIframe ?? 0},${r.enemyAttacksMissedByPhase ?? 0},${r.firstBloodTime ?? 0},${r.firstDamageTakenTime ?? 0},${r.longestStretchBetweenHitsMs ?? 0},${_esc(r.engineFramePerfJson)},${_esc(r.botPositionTrailJson)},${_esc(r.stuckEventsJson)},${_esc(r.botMapCellVisitsJson)},${_esc(r.decisionTraceJson)}\n`;
  }

  // ── Download ──
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `bot_full_data_${allRuns.length}runs_${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);

  console.log(`%c[BOT] 📊 Downloaded CSV with ${allRuns.length} runs, 11 data sheets (BETA 14.61 converge)`, 'color:#39ff14;font-size:12px');
}

/** Get all run data as JSON (for programmatic access) */
export function getAllRunData(): RunData[] {
  return [...allRuns];
}

/** Get cross-run analytics */
export function getAnalytics(): CrossRunAnalytics {
  return computeCrossRunAnalytics();
}

export function getBotReport(): string {
  if (allRuns.length === 0) return 'No runs completed.';
  const a = computeCrossRunAnalytics();
  return JSON.stringify(a, null, 2);
}
